import React, { useState } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Box,
  IconButton,
  Divider,
  Tooltip,
  Select,
  MenuItem,
  useTheme as useMuiTheme,
  Switch,
  Menu,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { 
  NotificationsNone as NotificationsNoneIcon,
  Logout as LogoutIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  ManageAccounts as ManageAccountsIcon
} from '@mui/icons-material';
import { useMsal } from '@azure/msal-react';
import { useUserInfo } from '../hooks/useUserInfo';
import { useMetadataContext } from '../context/MetadataContext';
import AdminPanelDialog from './AdminPanelDialog';

// Import logos from assets
import unileverLogo from '../assets/Unilever-Logo.png';
import profileImage from '../assets/profile.png';
import accentureLogo from '../assets/accenture.png';
// import microsoftLogo from '../assets/microsoft.png';
// import nvidiaLogo from '../assets/nvidia.png';

const Header = () => {
  const theme = useMuiTheme();
  const isDark = theme.palette.mode === 'dark';
  const { plants, selectedPlantId, setSelectedPlantId, isLoading: loadingPlants } = useMetadataContext();
  const [languageEnabled, setLanguageEnabled] = useState(true);
  const { instance } = useMsal();
  const { username, allRoles } = useUserInfo();

  // Profile dropdown state
  const [profileAnchorEl, setProfileAnchorEl] = useState<null | HTMLElement>(null);
  const [adminDialogOpen, setAdminDialogOpen] = useState(false);
  const isProfileMenuOpen = Boolean(profileAnchorEl);

  // Check if user is admin
  // const isAdmin = allRoles.includes('Admin');
  const isAdmin = true; // Temporarily enabled for all users

  // Dropdown enabled ONLY if user has BusinessUser role
  // All other roles (PlantHead, Operator) are disabled
  const canChangeDropdown = allRoles.includes('BusinessUser');

  const handlePlantChange = (event: any) => {
    setSelectedPlantId(event.target.value);
  };

  const handleLanguageToggle = (event: any) => {
    setLanguageEnabled(event.target.checked);
  };

  const handleProfileClick = (event: React.MouseEvent<HTMLElement>) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileAnchorEl(null);
  };

  const handleAdminPanelClick = () => {
    handleProfileMenuClose();
    if (isAdmin) {
      setAdminDialogOpen(true);
    }
  };

  const handleAdminDialogClose = () => {
    setAdminDialogOpen(false);
  };

  const handleLogout = () => {
    // Clear persisted machine tab selection on logout
    try {
      Object.keys(sessionStorage).forEach((key) => {
        if (key.startsWith('selectedMachineKey_')) {
          sessionStorage.removeItem(key);
        }
      });
    } catch {
      // Silently fail if sessionStorage is unavailable
    }
    instance.logoutRedirect({
      postLogoutRedirectUri: '/',
    }).catch((error) => {
      console.error('Logout failed:', error);
    });
  };

  return (
    <AppBar 
      position="static" 
      elevation={0}
      sx={{ 
        bgcolor: isDark ? 'rgba(40, 40, 62, 0.6)' : theme.palette.primary.main,
        minHeight: '56px',
        maxHeight: '56px',
      }}
    >
      <Toolbar 
        sx={{ 
          minHeight: '56px',
          maxHeight: '56px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3
        }}
      >
        {/* Left Group - Logo, Divider, Plant Dropdown */}
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            flexShrink: 0
          }}
        >
          {/* Unilever Logo */}
          <Box
            component="img"
            src={unileverLogo}
            alt="Unilever"
            fetchPriority="high"
            sx={{
              width: '32px',
              height: '32px',
              objectFit: 'contain',
              flexShrink: 0
            }}
          />
          
          {/* Vertical Divider */}
          <Divider 
            orientation="vertical" 
            flexItem 
            sx={{ 
              height: '32px',
              alignSelf: 'center',
              bgcolor: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
              flexShrink: 0
            }} 
          />
          
          {/* Plant Label and Dropdown */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography 
              sx={{ 
                fontSize: '18px',
                fontWeight: 700,
                color: theme.palette.text.primary,
                whiteSpace: 'nowrap'
              }}
            >
              Plant :
            </Typography>
            
            <Select
              value={selectedPlantId ?? ''}
              onChange={handlePlantChange}
              disabled={!canChangeDropdown || loadingPlants}
              size="small"
              IconComponent={KeyboardArrowDownIcon}
              sx={{
                width: '160px',
                height: '34px',
                
                color: theme.palette.text.primary,
                border: `1px solid ${theme.palette.text.primary}`,
                borderRadius: '4px',
                backgroundColor: '#030712',
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                },
                '&.Mui-disabled': {
                  backgroundColor: '#030712',
                  opacity: 0.6,
                  cursor: 'not-allowed'
                },
                '& .MuiSelect-select': {
                  py: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '14px',
                },
                '& .MuiSvgIcon-root': {
                  color: theme.palette.text.primary
                }
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: isDark ? '#192033' : theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    '& .MuiMenuItem-root': {
                      fontSize: '14px',
                      '&:hover': {
                        bgcolor: theme.palette.action.hover
                      },
                      '&.Mui-selected': {
                        bgcolor: theme.palette.action.selected,
                        '&:hover': {
                          bgcolor: theme.palette.action.selected
                        }
                      }
                    }
                  }
                }
              }}
            >
              {plants.map((plant) => (
                <MenuItem key={plant.plant_id} value={plant.plant_id}>
                  {plant.plant_name}
                </MenuItem>
              ))}
            </Select>
          </Box>

          {/* Partnership Logos */}
          <Box 
            sx={{ 
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              ml: 2
            }}
          >
            {/* Accenture Logo */}
            <Box
              component="img"
              src={accentureLogo}
              alt="Accenture"
              fetchPriority="high"
              sx={{
                height: '20px',
                width: 'auto',
                objectFit: 'contain'
              }}
            />
            
            {/* NVIDIA Logo */}
            {/* <Box
              component="img"
              src={nvidiaLogo}
              alt="NVIDIA"
              sx={{
                height: '20px',
                width: 'auto',
                objectFit: 'contain'
              }}
            /> */}
            
            {/* Microsoft Logo */}
            {/* <Box
              component="img"
              src={microsoftLogo}
              alt="Microsoft"
              sx={{
                height: '20px',
                width: 'auto',
                objectFit: 'contain'
              }}
            /> */}
          </Box>
        </Box>

        {/* Right Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
          {/* Language Toggle with Label */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Switch
              checked={languageEnabled}
              onChange={handleLanguageToggle}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#fff',
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: '#fff',
                },
              }}
            />
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: 700,
                color: theme.palette.text.primary,
                whiteSpace: 'nowrap'
              }}
            >
              English
            </Typography>
          </Box>

          {/* Notification Icon */}
          <Tooltip title="Notifications">
            <IconButton 
              sx={{ 
                color: theme.palette.text.primary,
                '&:hover': { bgcolor: theme.palette.action.hover }
              }}
            >
              <NotificationsNoneIcon />
            </IconButton>
          </Tooltip>
          
          {/* Logout Icon */}
          <Tooltip title="Logout">
            <IconButton 
              onClick={handleLogout}
              sx={{ 
                color: theme.palette.text.primary,
                '&:hover': { bgcolor: theme.palette.action.hover }
              }}
            >
              <LogoutIcon />
            </IconButton>
          </Tooltip>
          
          {/* User Profile Image with Username and Dropdown */}
          <Tooltip title="Profile">
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
              onClick={handleProfileClick}
            >
              <Box
                component="img"
                src={profileImage}
                alt="Profile"
                sx={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  transition: 'opacity 0.2s',
                  '&:hover': {
                    opacity: 0.8,
                  }
                }}
              />
              {username && (
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 700,
                    color: theme.palette.text.primary,
                    whiteSpace: 'nowrap'
                  }}
                >
                  {username}
                </Typography>
              )}
            </Box>
          </Tooltip>

          {/* Profile Dropdown Menu */}
          <Menu
            anchorEl={profileAnchorEl}
            open={isProfileMenuOpen}
            onClose={handleProfileMenuClose}
            onClick={handleProfileMenuClose}
            PaperProps={{
              elevation: 3,
              sx: {
                mt: 1.5,
                minWidth: 200,
                bgcolor: isDark ? '#192033' : theme.palette.background.paper,
                '& .MuiMenuItem-root': {
                  px: 2,
                  py: 1.5,
                  gap: 1.5,
                  fontSize: '14px',
                  '&:hover': {
                    bgcolor: theme.palette.action.hover,
                  },
                  '&.Mui-disabled': {
                    opacity: 0.5,
                  }
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem
              onClick={handleAdminPanelClick}
              disabled={false}
            >
              <ListItemIcon>
                <ManageAccountsIcon 
                  fontSize="small" 
                  sx={{ color: theme.palette.text.primary }} 
                />
              </ListItemIcon>
              <ListItemText>Admin Panel</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>

      {/* Admin Panel Dialog */}
      <AdminPanelDialog 
        open={adminDialogOpen} 
        onClose={handleAdminDialogClose} 
      />
    </AppBar>
  );
};

export default Header;